import { useQuery } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";

import type { HomeDnaProjectConfigurationDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { createRequiredStringRule } from "@/helpers/rules";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export type HomeDnaFormValues = HomeDnaProjectConfigurationDto;

export function HomeDnaPage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const integrationQueries = useIntegrationQueries();

  const { data, isFetching, error } = useQuery(integrationQueries.homeDnaConfiguration());
  const mutation = integrationMutations.useEditHomeDnaConfiguration();

  if (!hasPermission((x) => x.integrations.canManageHomeDna)) {
    return <ErrorPage status={404} />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isFetching) {
    return <FullSizeLoader withPadding />;
  }

  const name = t("page.integration.homedna.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <HomeDnaForm
        defaultValues={{
          enabled: data?.enabled || false,
          clientTenant: data?.clientTenant || "",
          clientSecret: data?.clientSecret || "",
          buyerGln: data?.buyerGln || "",
          buyerName: data?.buyerName || "",
          contractorGln: data?.contractorGln || "",
          contractorName: data?.contractorName || "",
        }}
        isSubmitting={mutation.isPending}
        onSubmit={mutation.mutate}
      />
    </DocumentPage>
  );
}

function HomeDnaForm({
  defaultValues,
  isSubmitting,
  onSubmit,
}: {
  defaultValues: Partial<HomeDnaFormValues>;
  isSubmitting: boolean;
  onSubmit: (data: HomeDnaFormValues) => void;
}): React.ReactNode {
  const { t } = useTranslation();
  const formMethods = useForm<HomeDnaFormValues>({ defaultValues });
  const enabled = useWatch({ control: formMethods.control, name: "enabled" });

  return (
    <Form.Root formMethods={formMethods} onSubmit={onSubmit}>
      <Form.Container title={t("page.integration.homedna.title")}>
        <Form.Body>
          <FormCheckbox<HomeDnaFormValues, "enabled">
            id="enabled"
            name="enabled"
            label={t("common.action.enable")}
            onChange={(e) => {
              if (enabled && !e.target.checked) {
                formMethods.clearErrors();
              }
            }}
          />

          <FormField label={t("page.integration.homedna.form.client-tenant")} required>
            <FormInput<HomeDnaFormValues, "clientTenant">
              id="clientTenant"
              name="clientTenant"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.homedna.form.client-tenant")
                    : () => undefined,
                },
              }}
              disabled={!enabled}
            />
          </FormField>

          <FormField label={t("page.integration.homedna.form.client-secret")} required>
            <FormInput<HomeDnaFormValues, "clientSecret">
              id="clientSecret"
              name="clientSecret"
              type="password"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.homedna.form.client-secret")
                    : () => undefined,
                },
              }}
              disabled={!enabled}
            />
          </FormField>

          <FormField label={t("page.integration.homedna.form.buyer-gln")} required>
            <FormInput<HomeDnaFormValues, "buyerGln">
              name="buyerGln"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.homedna.form.buyer-gln")
                    : () => undefined,
                  mustOnlyContainNumbers: (value) => {
                    if (!enabled || !value) {
                      return undefined;
                    }

                    return /^\d+$/.test(value) ? undefined : t("components.form.error.must-only-contain-numbers");
                  },
                },
              }}
              placeholder="0000000000000"
              disabled={!enabled}
            />
          </FormField>

          <FormField label={t("page.integration.homedna.form.buyer-name")} required>
            <FormInput<HomeDnaFormValues, "buyerName">
              name="buyerName"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.homedna.form.buyer-name")
                    : () => undefined,
                },
                maxLength: {
                  message: t("components.form.error.max-length", { length: 100 }),
                  value: 100,
                },
              }}
              disabled={!enabled}
            />
          </FormField>

          <FormField label={t("page.integration.homedna.form.contractor-gln")} required>
            <FormInput<HomeDnaFormValues, "contractorGln">
              id="contractorGln"
              name="contractorGln"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.homedna.form.contractor-gln")
                    : () => undefined,
                  mustOnlyContainNumbers: (value) => {
                    if (!enabled || !value) {
                      return undefined;
                    }

                    return /^\d+$/.test(value) ? undefined : t("components.form.error.must-only-contain-numbers");
                  },
                },
              }}
              placeholder="0000000000000"
              disabled={!enabled}
            />
          </FormField>

          <FormField label={t("page.integration.homedna.form.contractor-name")} required>
            <FormInput<HomeDnaFormValues, "contractorName">
              id="contractorName"
              name="contractorName"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.homedna.form.contractor-name")
                    : () => undefined,
                },
                maxLength: {
                  message: t("components.form.error.max-length", { length: 100 }),
                  value: 100,
                },
              }}
              disabled={!enabled}
            />
          </FormField>
        </Form.Body>
        <Form.Footer
          renderPrimaryAction={() => (
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.action.save")}
            </Button>
          )}
        />
      </Form.Container>
    </Form.Root>
  );
}
